import { getLandingSite } from 'dibs-cookie-jar/exports/dibsSession.ts';
import { type EcomProduct, type ShippingAddress } from '../types.ts';
import { pushElevarEvent } from './dataLayer.ts';
import { mapProducts, mapClickProducts, mapImpressions } from './productMapper.ts';
import {
    getElevarUserProperties,
    getElevarAuthUserProperties,
    getElevarCurrencyCode,
    hasPushedElevarAuthEvent,
    markElevarAuthEventPushed,
    mapAddressToElevarCustomerFields,
} from './userProperties.ts';
import { ELEVAR_LIST_SEARCH_RESULTS, type ElevarPurchaseActionField } from './types.ts';
import { toStr } from './helpers.ts';

export const pushElevarUserData = ({
    currencyCode,
    cartTotal,
    cartProducts,
}: {
    currencyCode: string;
    cartTotal?: string;
    cartProducts?: ReadonlyArray<EcomProduct>;
}): void => {
    pushElevarEvent({
        event: 'dl_user_data',
        user_properties: getElevarUserProperties(),
        cart_total: toStr(cartTotal) ?? '0',
        ecommerce: {
            currencyCode,
            cart_contents: { products: mapProducts(cartProducts, 'cart_contents') },
        },
    });
};

export const pushElevarSignUp = (): void => {
    if (hasPushedElevarAuthEvent('dl_sign_up')) {
        return;
    }
    const userProperties = getElevarAuthUserProperties();
    if (!userProperties) {
        return;
    }
    markElevarAuthEventPushed('dl_sign_up');
    pushElevarEvent({
        event: 'dl_sign_up',
        user_properties: userProperties,
    });
};

export const pushElevarLogin = (): void => {
    if (hasPushedElevarAuthEvent('dl_login')) {
        return;
    }
    const userProperties = getElevarAuthUserProperties();
    if (!userProperties) {
        return;
    }
    markElevarAuthEventPushed('dl_login');
    pushElevarEvent({
        event: 'dl_login',
        user_properties: userProperties,
    });
};

export const pushElevarViewItemList = ({
    products,
    currencyCode,
}: {
    products: ReadonlyArray<EcomProduct>;
    currencyCode: string;
}): void => {
    pushElevarEvent({
        event: 'dl_view_item_list',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            impressions: mapImpressions(products),
        },
    });
};

export const pushElevarViewSearchResults = ({
    products,
    currencyCode,
    list = ELEVAR_LIST_SEARCH_RESULTS,
}: {
    products: ReadonlyArray<EcomProduct>;
    currencyCode: string;
    list?: string;
}): void => {
    pushElevarEvent({
        event: 'dl_view_search_results',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            actionField: { list },
            impressions: mapImpressions(products),
        },
    });
};

export const pushElevarSelectItem = ({
    products,
    list,
    currencyCode,
}: {
    products: ReadonlyArray<EcomProduct>;
    currencyCode: string;
    list?: string;
}): void => {
    pushElevarEvent({
        event: 'dl_select_item',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            click: {
                actionField: { list: list ?? '', action: 'click' as const },
                products: mapClickProducts(products),
            },
        },
    });
};

export const pushElevarViewItem = ({
    products,
    list,
    currencyCode,
}: {
    products: ReadonlyArray<EcomProduct>;
    currencyCode: string;
    list?: string;
}): void => {
    pushElevarEvent({
        event: 'dl_view_item',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            detail: {
                actionField: { list: list ?? '', action: 'detail' as const },
                products: mapProducts(products, 'view_item'),
            },
        },
    });
};

export const pushElevarAddToCart = ({
    products,
    list,
    currencyCode,
}: {
    products: ReadonlyArray<EcomProduct>;
    currencyCode: string;
    list?: string;
}): void => {
    pushElevarEvent({
        event: 'dl_add_to_cart',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            cart_contents: { products: [] },
            add: {
                actionField: { list: list ?? '' },
                products: mapProducts(products, 'add_to_cart'),
            },
        },
    });
};

export const pushElevarRemoveFromCart = ({
    products,
    list,
    currencyCode,
}: {
    products: ReadonlyArray<EcomProduct>;
    currencyCode: string;
    list?: string;
}): void => {
    pushElevarEvent({
        event: 'dl_remove_from_cart',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            remove: {
                actionField: { list: list ?? '' },
                products: mapProducts(products, 'remove_from_cart'),
            },
        },
    });
};

export const pushElevarViewCart = ({
    products,
    cartTotal,
    currencyCode,
}: {
    products: ReadonlyArray<EcomProduct>;
    currencyCode: string;
    cartTotal?: string;
}): void => {
    pushElevarEvent({
        event: 'dl_view_cart',
        user_properties: getElevarUserProperties(),
        cart_total: toStr(cartTotal) ?? '0.0',
        ecommerce: {
            currencyCode,
            actionField: {},
            impressions: mapImpressions(products, 'view_cart'),
        },
    });
};

const ELEVAR_CHECKOUT_STEPS = {
    dl_begin_checkout: '1',
    dl_add_shipping_info: '2',
    dl_add_payment_info: '3',
} as const;

type ElevarCheckoutEvent = keyof typeof ELEVAR_CHECKOUT_STEPS;
type ElevarCheckoutProducts = ReadonlyArray<EcomProduct>;

// GTM fires its checkout steps on pane changes, Elevar on the step CTAs, so these are pushed
// directly instead of going through the ecommerce bridge.
const pushElevarCheckoutStep = (
    event: ElevarCheckoutEvent,
    products: ElevarCheckoutProducts
): void => {
    if (!products.length) {
        return;
    }
    pushElevarEvent({
        event,
        user_properties: getElevarUserProperties(),
        marketing: { landing_site: getLandingSite() },
        ecommerce: {
            currencyCode: getElevarCurrencyCode(),
            checkout: {
                actionField: { step: ELEVAR_CHECKOUT_STEPS[event] },
                products: mapProducts(products, 'checkout'),
            },
        },
    });
};

export const trackElevarBeginCheckout = (products: ElevarCheckoutProducts): void =>
    pushElevarCheckoutStep('dl_begin_checkout', products);

export const trackElevarAddShippingInfo = (products: ElevarCheckoutProducts): void =>
    pushElevarCheckoutStep('dl_add_shipping_info', products);

export const trackElevarAddPaymentInfo = (products: ElevarCheckoutProducts): void =>
    pushElevarCheckoutStep('dl_add_payment_info', products);

export const pushElevarPurchase = ({
    products,
    actionField,
    currencyCode,
    shippingAddress,
}: {
    products: ReadonlyArray<EcomProduct>;
    actionField: ElevarPurchaseActionField;
    currencyCode: string;
    shippingAddress?: ShippingAddress | null;
}): void => {
    pushElevarEvent({
        event: 'dl_purchase',
        user_properties: shippingAddress
            ? {
                  ...getElevarUserProperties(),
                  ...mapAddressToElevarCustomerFields(shippingAddress),
              }
            : getElevarUserProperties(),
        marketing: { landing_site: getLandingSite() },
        ecommerce: {
            currencyCode,
            purchase: {
                actionField,
                products: mapProducts(products, 'purchase'),
            },
        },
    });
};

export const pushElevarSubscribe = (email?: string): void => {
    const customerEmail = toStr(email) ?? toStr(getElevarUserProperties().customer_email);
    if (!customerEmail) {
        return;
    }
    pushElevarEvent({
        event: 'dl_subscribe',
        lead_type: 'email',
        user_properties: { customer_email: customerEmail },
    });
};
